import { Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { identify } from "../capture";
import { useAuth } from "../context/AuthContext";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getLogoMode, hostname, parseError, isSafeUrl, trackAffiliate } from "../utils";

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function LoginSso({ mode }: { mode: string }) {
  const [domain, setDomain] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();
  const { whitelabelLogos, whitelabelSettings } = useWhitelabel();
  const { signIn } = useAuth();
  let query = useQuery();

  const getLoginLogo = () => {
    // Use whitelabel horizontal logo if available, otherwise use default logo
    // Use dark mode logo if in dark mode and available
    if (mode === 'dark' && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  useEffect(() => {
    const token = query.get("token");
    if (token && !processing) {
      setProcessing(true);
      // The token from SSO is already a custom token we can use directly
      // We need to verify it with the backend first
      axios.post(`${hostname}/auth/sso/verify`, { token })
        .then(async (response) => {
          if (!response.data.success) {
            setErrorMessage(response.data.error || "Invalid token");
            setProcessing(false);
            return;
          }

          const { email, uid, customToken } = response.data;

          // Sign in with the token using AuthContext
          signIn(customToken, { email, uid });

          // Track analytics
          identify({ uid, email });
          trackAffiliate({ uid, email }, whitelabelSettings);

          // Check if user has companies and subscription to determine routing
          try {
            const [companiesResponse, usageResponse] = await Promise.all([
              axios.get(`${hostname}/companies`, {
                headers: { Authorization: `Bearer ${customToken}` },
              }),
              axios.get(`${hostname}/usage`, {
                headers: { Authorization: `Bearer ${customToken}` },
              }),
            ]);

            const hasCompanies = companiesResponse.data.companies && companiesResponse.data.companies.length > 0;
            const subscriptionType = usageResponse.data.usage?.subscriptionType || 'free';
            const isFree = subscriptionType.toLowerCase() === 'free' || subscriptionType === '';

            if (hasCompanies) {
              // User has companies, go to dashboard
              navigate("/dashboard/companies");
            } else if (whitelabelSettings?.disableFreeAccounts && !whitelabelSettings?.customSubscriptionEnabled && isFree) {
              // Only go to /subscribe if free accounts disabled AND user doesn't have a subscription
              console.log('[LoginSso] Free accounts disabled and no subscription - redirecting to /subscribe');
              navigate("/subscribe");
            } else {
              console.log('[LoginSso] Redirecting to /onboarding');
              navigate("/onboarding");
            }
          } catch (checkError) {
            console.error("Error checking companies:", checkError);
            // On error, go to dashboard which will handle the error state
            // DO NOT go to onboarding on error - only go to onboarding if the request succeeds and returns empty
            navigate("/dashboard/companies");
          }
        })
        .catch((err) => {
          console.error("SSO token verification failed:", err);
          setErrorMessage("Invalid token");
          setProcessing(false);
        });
    }
  }, [query, processing, signIn, navigate, whitelabelSettings]);

  const submitForm = (e: any) => {
    e.preventDefault();
    setErrorMessage("");
    axios
      .post(`${hostname}/sso/login`, {
        domain,
      })
      .then((res) => {
        const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
        if (safeUrlPattern.test(res.data.redirectUrl)) window.location.href = res.data.redirectUrl;
      })
      .catch((err) => {
        setErrorMessage(parseError(err));
      });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
      }}
    >
      <Paper
        style={{
          width: 300,
          top: "50%",
          left: "50%",
          position: "fixed",
          marginTop: -150,
          marginLeft: -150,
          padding: 10,
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
            }}
          >
            <Box
              sx={{
                p: 4,
                display: "flex",
                justifyItems: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <img
                src={getLoginLogo()}
                alt="logo"
                style={{ objectFit: "contain", height: "100%", width: "100%" }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} style={{ textAlign: "center" }}>
            <Typography variant="h6" component={"h6"}>
              SSO Login
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            style={{ textAlign: "center", marginTop: 20, marginBottom: 20 }}
          >
            <form onSubmit={submitForm}>
              <TextField
                fullWidth
                label="Domain"
                onChange={(e) => setDomain(e.target.value)}
                value={domain}
                required
                style={{ marginBottom: 10 }}
              />
              {errorMessage && (
                <Typography style={{ marginBottom: 10 }}>
                  {errorMessage}
                </Typography>
              )}
              <Button variant="outlined" type="submit" fullWidth>
                Login
              </Button>
            </form>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
